/**
 * This callback is displayed as a global member.
 * @callback ForeachCallbackDtMap
 * @param {any} item
 * @param {DtModel} model
 * @param {number} dbId
 */

const _map = new Map();

/**
 * A two-level map keyed by a compound (model, dbId) key. Shared by the HUD assets layer (label bookkeeping)
 * and the TrackedElementsProvider (per-element data). Kept in its own module so both can import it without a cycle.
 */
export class DtMap {
    constructor() { this.map = new Map(); }

    /** Creates a shallow iterator for the idSet described by the keys in this map. */
    asIdSetIterator() {
        _map.clear();
        for (const [model, dbIds] of this.map) {
            _map.set(model, dbIds.keys());
        }
        return _map;
    }

    /** Clears map */
    clear() { this.map.clear(); }

    /** Shallow clone key-entries maps */
    clone() {
        const tmp = new DtMap();
        for (const [model, dbIds] of this.map) {
            tmp.map.set(model, new Map(dbIds));
        }
        return tmp;
    }

    /** Remove entry */
    delete(model, dbId) {
        const byDbId = this.map.get(model);
        if (byDbId && byDbId.has(dbId)) {
            byDbId.delete(dbId);
        }
    }

    /** Get element from compound key */
    get(model, dbId) {
        return this.map.get(model)?.get(dbId);
    }

    /** Get element and set parameter if missing */
    getOrSet(model, dbId, orSet) {
        let byDbId = this.map.get(model);
        if (!byDbId) this.map.set(model, byDbId = new Map());

        let item = byDbId.get(dbId);
        if (item == null) byDbId.set(dbId, item = orSet);

        return item;
    }

    /**
     * Foreach entries, vaguely matches Map.forEach
     * @param {ForeachCallbackDtMap} foreachFn
     */
    forEach(foreachFn) {
        for (const [model, byDbId] of this.map) {
            for (const [dbId, item] of byDbId) {
                foreachFn(item, model, dbId);
            }
        }
    }

    /** Set entry by compound key */
    set(model, dbId, item) {
        let byDbId = this.map.get(model);
        if (!byDbId) this.map.set(model, byDbId = new Map());

        byDbId.set(dbId, item);
        return this;
    }

    /** Foreach values, vaguely matches Map.values */
    * values() {
        for (const byDbId of this.map.values()) {
            yield* byDbId.values();
        }
    }
}
